import { useState, useEffect } from 'react';
import { LogoLockup } from './components/Brand';
import LoginScreen from './components/auth/LoginScreen';
import TripEntryForm from './components/trips/TripEntryForm';
import SuggestedTrips from './components/trips/SuggestedTrips';
import TripHistory from './components/trips/TripHistory';
import Billable from './components/trips/Billable';
import Export from './components/trips/Export';
import Reimbursement from './components/trips/Reimbursement';
import AddressMappings from './components/addresses/AddressMappings';
import { Button } from './components/ui/button';

// API base — in dev, proxy handles this; in prod, same origin
const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3010/api';

const api = {
  get: async (endpoint, token) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}${endpoint}`, { headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('milewise_token', refreshedToken);
    return response.json();
  },

  post: async (endpoint, data, token) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('milewise_token', refreshedToken);
    return response.json();
  },

  put: async (endpoint, data, token) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT', headers, body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const refreshedToken = response.headers.get('X-Refreshed-Token');
    if (refreshedToken) localStorage.setItem('milewise_token', refreshedToken);
    return response.json();
  },

  delete: async (endpoint, token) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE', headers
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }
};

const NAV_ITEMS = [
  { key: 'entry', label: 'New Trip' },
  { key: 'suggested', label: 'Suggested Trips' },
  { key: 'history', label: 'Trip History' },
  { key: 'billable', label: 'Billable' },
  { key: 'reimbursement', label: 'Reimbursement' },
  { key: 'addresses', label: 'Address Mappings' },
  { key: 'export', label: 'Export' },
];

const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [currentView, setCurrentView] = useState(() => localStorage.getItem('milewise_view') || 'entry');
  const [syncroPending, setSyncroPending] = useState(0);

  // Sync dark mode class on <html> element
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  // Remember the active tab so a page refresh stays on the same view
  useEffect(() => {
    localStorage.setItem('milewise_view', currentView);
  }, [currentView]);

  useEffect(() => {
    const savedToken = localStorage.getItem('milewise_token');
    const savedUser = localStorage.getItem('milewise_user');
    if (savedToken && savedUser) {
      api.get('/health', savedToken)
        .then(() => {
          setUser(JSON.parse(savedUser));
          setToken(savedToken);
        })
        .catch(() => {
          localStorage.removeItem('milewise_token');
          localStorage.removeItem('milewise_user');
        });
    }
  }, []);

  // Auto-pull from Syncro on load (server throttles to once/day) and keep the badge count fresh.
  useEffect(() => {
    if (!token) return;
    api.post('/syncro/sync', {}, token)
      .then(r => setSyncroPending(r.pending || 0))
      .catch(() => { /* Syncro not configured / unreachable — leave badge empty */ });
  }, [token]);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('milewise_user', JSON.stringify(userData));
    localStorage.setItem('milewise_token', userToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('milewise_username');
    localStorage.removeItem('milewise_password');
    localStorage.removeItem('milewise_user');
    localStorage.removeItem('milewise_token');
    setCurrentView('entry');
  };

  if (!user) {
    return <LoginScreen onLogin={handleLogin} api={api} />;
  }

  const renderView = () => {
    switch (currentView) {
      case 'entry':
        return <TripEntryForm user={user} token={token} api={api} onNavigate={setCurrentView} />;
      case 'suggested':
        return <SuggestedTrips user={user} token={token} api={api} onPendingChange={setSyncroPending} />;
      case 'history':
        return <TripHistory user={user} token={token} api={api} />;
      case 'billable':
        return <Billable user={user} token={token} api={api} />;
      case 'export':
        return <Export user={user} token={token} />;
      case 'reimbursement':
        return <Reimbursement user={user} token={token} api={api} />;
      case 'addresses':
        return <AddressMappings user={user} token={token} api={api} />;
      default:
        return <TripEntryForm user={user} token={token} api={api} onNavigate={setCurrentView} />;
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation Bar */}
      <nav className="border-b bg-card px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          <LogoLockup iconHeight={28} className="mr-4" />
          {NAV_ITEMS.map(item => (
            <Button
              key={item.key}
              variant={currentView === item.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setCurrentView(item.key)}
            >
              {item.label}
              {item.key === 'suggested' && syncroPending > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold h-4 min-w-4 px-1">
                  {syncroPending}
                </span>
              )}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsDarkMode(prev => !prev)}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {isDarkMode ? 'Light' : 'Dark'}
          </button>
          <span className="text-sm text-muted-foreground">{user.displayName}</span>
          <Button variant="ghost" size="sm" onClick={handleLogout}>Logout</Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-4">
        {renderView()}
      </main>
    </div>
  );
};

export default App;
